// viewportsArrayToRecord, layoutsArrayToRecord, and stepFromGqlFormat are mirrored in
// graphql/src/util/viewer/hangingProtocol/stepConversions.ts — apply changes to both files.

import type {
  StepConfigDetails as GqlStepConfigDetails,
  StepViewportEntry,
  StepLayoutEntry,
  StepSubStep,
  StepConfiguration as GqlStepConfiguration,
} from 'generated/graphql';

export type StepConfigDetails = GqlStepConfigDetails;

export interface ViewportConfigurationEntry
  extends Omit<StepViewportEntry, 'viewport_id' | '__typename'> {
  /** Marks this viewport as intentionally cleared. Stripped before backend send. */
  is_clear?: boolean;
}

/** Viewport overrides per step for prior scrolling. */
export type SubStep = Record<string, ViewportConfigurationEntry>;

/** Record-based step config used for frontend state (viewports/layouts keyed by ID, not arrays). */
export interface StepConfiguration {
  smid: string | null;
  display_name: string | null;
  layouts: Record<string, string>;
  config_details: StepConfigDetails;
  viewports: Record<string, ViewportConfigurationEntry>;
  order?: number;
  sub_steps: SubStep[] | null;
}

export function viewportsArrayToRecord(
  viewports: ReadonlyArray<StepViewportEntry>
): Record<string, ViewportConfigurationEntry> {
  const record: Record<string, ViewportConfigurationEntry> = {};
  for (const vp of viewports) {
    record[vp.viewport_id] = {
      display_name: vp.display_name,
      study_smid: vp.study_smid ?? null,
      series_smid: vp.series_smid ?? null,
      ...(vp.view_type != null ? { view_type: vp.view_type } : {}),
      ...(vp.series_criteria_source_text != null
        ? { series_criteria_source_text: vp.series_criteria_source_text }
        : {}),
      ...(vp.comparison_index != null ? { comparison_index: vp.comparison_index } : {}),
      ...(vp.criteria?.length
        ? { criteria: vp.criteria.map((c) => ({ category: c.category, value: c.value })) }
        : {}),
      ...(vp.stack_smid != null ? { stack_smid: vp.stack_smid } : {}),
      ...(vp.viewport_mirror_group_id != null
        ? { viewport_mirror_group_id: vp.viewport_mirror_group_id }
        : {}),
      ...(vp.match_justification != null ? { match_justification: vp.match_justification } : {}),
    };
  }
  return record;
}

export function layoutsArrayToRecord(
  layouts: ReadonlyArray<StepLayoutEntry>
): Record<string, string> {
  const record: Record<string, string> = {};
  for (const l of layouts) {
    record[l.window_id] = l.layout;
  }
  return record;
}

export function stepFromGqlFormat(gqlStep: GqlStepConfiguration): StepConfiguration {
  const layouts = layoutsArrayToRecord(gqlStep.layouts);
  const viewports = viewportsArrayToRecord(gqlStep.viewports);
  const subSteps = gqlStep.sub_steps
    ? gqlStep.sub_steps.map((ss) => viewportsArrayToRecord(ss.viewports))
    : null;

  return {
    smid: gqlStep.smid ?? null,
    display_name: gqlStep.display_name ?? null,
    layouts,
    config_details: gqlStep.config_details ?? {},
    viewports,
    order: gqlStep.order ?? undefined,
    sub_steps: subSteps,
  };
}

/** Parses a layout string (e.g., "2,3") into a [rows, cols] tuple; clamps each to minimum 1. */
export function parseLayoutString(layoutStr: string): [number, number] {
  const [rawRows, rawCols] = layoutStr.split(',', 2);
  const rows = Number(rawRows);
  const cols = Number(rawCols);
  return [
    Number.isFinite(rows) && rows >= 1 ? rows : 1,
    Number.isFinite(cols) && cols >= 1 ? cols : 1,
  ];
}

export function formatLayoutString(layout: readonly [number, number]): string {
  return `${layout[0]},${layout[1]}`;
}

export type { GqlStepConfiguration, StepViewportEntry, StepLayoutEntry, StepSubStep };
